'use client';
import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { categories, type Account } from '@curio/contracts';
import {
  event,
  resetAnalytics,
  post,
  clearAccount,
  localDb,
  updatePreference,
} from '@curio/client';
import { PlatformChoices, parsePlatforms } from './platform-preferences';
import { Button, Chip, ErrorMessage } from '@curio/ui';

export function Preferences({ account: a }: { account: Account }) {
  const client = useQueryClient();
  const router = useRouter();
  const [error, setError] = useState<unknown>();
  const sync = useQuery({
    queryKey: ['sync', a.id],
    queryFn: async () =>
      (await localDb()).actions.where('userId').equals(a.id).toArray(),
    refetchInterval: 5000,
  });
  const set = async (key: string, value: unknown) => {
    try {
      await updatePreference(client, a, key, value);
    } catch (e) {
      setError(e);
      await client.invalidateQueries({ queryKey: ['me'] });
    }
  };
  const toggle = (list: string[], item: string) =>
    list.includes(item) ? list.filter((x) => x !== item) : [...list, item];
  const interests = (a.preferences.interests?.value ?? []) as string[];
  const chosenPlatforms = (a.preferences.platforms?.value ?? []) as string[];
  return (
    <div className="preferences">
      <section className="preferences-section" aria-labelledby="interests">
        <h2 id="interests">Your interests</h2>
        <p>Products to review are picked from the categories you follow.</p>
        <div className="chips">
          {categories.map((c) => (
            <Chip
              active={interests.includes(c)}
              key={c}
              onClick={() => void set('interests', toggle(interests, c))}
            >
              {c}
            </Chip>
          ))}
        </div>
      </section>
      <section className="preferences-section" aria-labelledby="platforms">
        <h2 id="platforms">Where you use products</h2>
        <p>Only products available on these platforms are suggested.</p>
        <PlatformChoices
          value={parsePlatforms(chosenPlatforms)}
          onChange={(next) => void set('platforms', next)}
        />
      </section>
      <section className="preferences-section" aria-labelledby="account">
        <h2 id="account">Account</h2>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={a.preferences.publicIdentity?.value === true}
            onChange={(e) => void set('publicIdentity', e.target.checked)}
          />
          Display my creator identity publicly
        </label>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={a.preferences.projectEmails?.value !== false}
            onChange={(e) => void set('projectEmails', e.target.checked)}
          />
          Email me about new feedback and save milestones for my projects
        </label>
        <ErrorMessage error={error} />
      </section>
      {!!sync.data?.length && (
        <section className="preferences-section panel">
          <h2>Pending changes</h2>
          {sync.data.map((action) => (
            <div key={action.id}>
              <p className="caption">
                {action.error || 'Waiting to sync when you’re online.'}
              </p>
              {action.error && (
                <div className="row">
                  <Button
                    size="sm"
                    onClick={async () => {
                      await (
                        await localDb()
                      ).actions.update(action.id, {
                        error: undefined,
                        ...(action.preference
                          ? {
                              preference: {
                                ...action.preference,
                                revision:
                                  a.preferences[action.preference.key]
                                    ?.revision ?? 0,
                              },
                              id: action.id,
                            }
                          : {}),
                      });
                      window.dispatchEvent(new Event('online'));
                      event('sync_action_retried', undefined, {});
                      await sync.refetch();
                    }}
                  >
                    Retry
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={async () => {
                      await (await localDb()).actions.delete(action.id);
                      event('sync_action_discarded', undefined, {});
                      await client.invalidateQueries();
                    }}
                  >
                    Discard
                  </Button>
                </div>
              )}
            </div>
          ))}
        </section>
      )}
      <div className="preferences-footer">
        <Button
          variant="ghost"
          onClick={async () => {
            await post('/auth/sign-out');
            resetAnalytics();
            document.cookie = 'curio_preview=; Max-Age=0; Path=/';
            await clearAccount(a.id);
            client.clear();
            router.push('/');
          }}
        >
          Sign out
        </Button>
      </div>
    </div>
  );
}
